import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCode, Search, Check, X, AlertCircle, Camera,
  CameraOff, User, Calendar, LogOut, Users, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../../lib/api';

const vibrate = (pattern) => {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch (e) {}
};

const ValidatorScannerPage = () => {
  const [validator, setValidator] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanMode, setScanMode] = useState('qr');
  const [inputValue, setInputValue] = useState('');
  const [validating, setValidating] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [overlayResult, setOverlayResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [stats, setStats] = useState(null);

  const html5QrCodeRef = useRef(null);
  const startingRef = useRef(false);
  const lastScannedRef = useRef(null);
  const validatingRef = useRef(false);
  const overlayTimerRef = useRef(null);
  const selectedEventRef = useRef(null);
  const scanModeRef = useRef('qr');

  useEffect(() => { selectedEventRef.current = selectedEvent; }, [selectedEvent]);
  useEffect(() => { scanModeRef.current = scanMode; }, [scanMode]);

  const fetchValidatorProfile = async () => {
    try {
      const response = await api.get('/producers/validators/me');
      setValidator(response.data);
      if (response.data.assigned_events?.length === 1) {
        setSelectedEvent(response.data.assigned_events[0]);
      }
    } catch (error) {
      console.error('Error fetching validator profile:', error);
      toast.error('Error al cargar perfil');
      if (error.response?.status === 401) {
        localStorage.removeItem('validatorToken');
        window.location.href = '/validador/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      const res = await api.get(`/producers/validators/events/${eventId}/scanner-stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Stats error:', e);
    }
  }, []);

  useEffect(() => {
    fetchValidatorProfile();
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedEvent) fetchStats(selectedEvent.id);
    else setStats(null);
  }, [selectedEvent, fetchStats]);

  const stopCamera = useCallback(async () => {
    const inst = html5QrCodeRef.current;
    html5QrCodeRef.current = null;
    setScanning(false);
    setCameraLoading(false);
    if (inst) {
      try { if (inst.isScanning) await inst.stop(); } catch (e) {}
      try { inst.clear(); } catch (e) {}
    }
  }, []);

  const handleValidate = useCallback(async (code) => {
    const event = selectedEventRef.current;
    if (!event || !code.trim() || validatingRef.current) return;

    validatingRef.current = true;
    setValidating(true);
    try {
      const response = await api.post('/producers/validators/verify-ticket', {
        event_id: event.id,
        ticket_code: code.trim(),
        method: scanModeRef.current
      });

      const result = {
        ...response.data,
        timestamp: new Date().toISOString(),
        searchCode: code
      };

      setLastResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 50));
      showOverlay(result);

      if (result.success) {
        vibrate(200);
      } else if (result.status === 'already_used') {
        vibrate([100, 60, 100]);
      } else {
        vibrate([80, 50, 80, 50, 80]);
      }

      setInputValue('');
      fetchStats(event.id);
    } catch (error) {
      console.error('Validation error:', error);
      const errorResult = {
        success: false,
        status: 'error',
        message: error.response?.data?.detail || 'Error al validar',
        timestamp: new Date().toISOString(),
        searchCode: code
      };
      setLastResult(errorResult);
      showOverlay(errorResult);
      vibrate([80, 50, 80, 50, 80]);
    } finally {
      validatingRef.current = false;
      setValidating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats]);

  const showOverlay = (result) => {
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    setOverlayResult(result);
    overlayTimerRef.current = setTimeout(() => setOverlayResult(null), 2800);
  };

  const dismissOverlay = () => {
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    setOverlayResult(null);
  };

  const onQrScanned = useCallback((decodedText) => {
    if (lastScannedRef.current === decodedText || validatingRef.current) return;
    lastScannedRef.current = decodedText;

    let codeToValidate = decodedText;
    const upper = decodedText.toUpperCase();
    if (upper.startsWith('BRAVO|INV|')) {
      codeToValidate = decodedText; // backend parses invitation format
    } else if (decodedText.includes('|')) {
      const parts = decodedText.split('|');
      if (parts.length >= 3) codeToValidate = parts[2];
    }

    handleValidate(codeToValidate);
    setTimeout(() => { lastScannedRef.current = null; }, 2500);
  }, [handleValidate]);

  const startCamera = useCallback(async () => {
    if (html5QrCodeRef.current || startingRef.current) return;
    if (!document.getElementById('qr-reader')) return;
    startingRef.current = true;
    setCameraLoading(true);
    try {
      const inst = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = inst;
      await inst.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (w, h) => {
            const m = Math.floor(Math.min(w, h) * 0.72);
            return { width: m, height: m };
          }
        },
        onQrScanned,
        () => {}
      );
      // If stop was requested while starting, shut down cleanly
      if (html5QrCodeRef.current !== inst) {
        try { await inst.stop(); } catch (e) {}
        try { inst.clear(); } catch (e) {}
      } else {
        setScanning(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (html5QrCodeRef.current) {
        try { html5QrCodeRef.current.clear(); } catch (e) {}
        html5QrCodeRef.current = null;
      }
      toast.error('No se pudo acceder a la cámara. Verificá los permisos.');
      setCameraEnabled(false);
    } finally {
      startingRef.current = false;
      setCameraLoading(false);
    }
  }, [onQrScanned]);

  // Auto start/stop camera based on mode, event and toggle
  useEffect(() => {
    if (scanMode === 'qr' && selectedEvent && cameraEnabled) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [scanMode, selectedEvent, cameraEnabled, startCamera, stopCamera]);

  const handleLogout = async () => {
    await stopCamera();
    localStorage.removeItem('validatorToken');
    window.location.href = '/validador/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!validator) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-bold text-white mb-2">Error de autenticación</h2>
          <p className="text-zinc-400 mb-4">Tu sesión ha expirado o no es válida</p>
          <Button onClick={() => window.location.href = '/validador/login'} className="bg-[#D4AF37] text-black">
            Iniciar sesión
          </Button>
        </div>
      </div>
    );
  }

  const overlayColor = overlayResult?.success
    ? 'bg-green-600'
    : overlayResult?.status === 'already_used'
    ? 'bg-yellow-600'
    : 'bg-red-600';

  return (
    <div className="min-h-screen bg-black pb-10">
      {/* Fullscreen result overlay */}
      <AnimatePresence>
        {overlayResult && (
          <motion.div
            data-testid="scan-result-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={dismissOverlay}
            className={`fixed inset-0 z-[100] flex flex-col items-center justify-center p-6 ${overlayColor}`}
          >
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 18 }}
              className="w-28 h-28 rounded-full bg-white/20 flex items-center justify-center mb-6"
            >
              {overlayResult.success ? (
                <Check size={72} className="text-white" strokeWidth={3} />
              ) : overlayResult.status === 'already_used' ? (
                <AlertCircle size={72} className="text-white" strokeWidth={2.5} />
              ) : (
                <X size={72} className="text-white" strokeWidth={3} />
              )}
            </motion.div>
            <p className="text-2xl font-black text-white text-center leading-tight mb-4" data-testid="overlay-message">
              {overlayResult.message}
            </p>
            {(overlayResult.ticket || overlayResult.invitation) && (
              <div className="bg-black/25 rounded-2xl px-6 py-4 text-center text-white space-y-1">
                <p className="text-xl font-bold">
                  {overlayResult.ticket?.buyer_name || overlayResult.invitation?.name}
                </p>
                <p className="text-white/90">
                  DNI: {overlayResult.ticket?.holder_dni || overlayResult.invitation?.dni || '—'}
                </p>
                {overlayResult.ticket?.tier_name && (
                  <p className="text-white/80 text-sm">{overlayResult.ticket.tier_name}</p>
                )}
                {overlayResult.invitation && (
                  <p className="text-white/80 text-sm">
                    Invitación · usos {overlayResult.invitation.redeemed_count}/{overlayResult.invitation.quantity}
                  </p>
                )}
              </div>
            )}
            <p className="text-white/70 text-xs mt-6">Tocá para seguir escaneando</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 p-4 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {validator.producer_logo ? (
              <img
                src={validator.producer_logo}
                alt={validator.producer_name}
                className="w-10 h-10 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center shrink-0">
                <QrCode className="text-[#D4AF37]" size={20} />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-white truncate">{validator.name}</p>
              <p className="text-xs text-zinc-500 truncate">{validator.producer_name}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400" data-testid="validator-logout-btn">
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Event Selector */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="event-selector-card">
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            <Calendar size={14} className="inline mr-2" />
            Evento
          </label>
          {(!validator.assigned_events || validator.assigned_events.length === 0) ? (
            <div className="text-yellow-400 text-sm flex items-start gap-2" data-testid="no-events-warning">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>No tenés eventos asignados o el evento asignado ya no existe. Contactá a tu productora para que te vincule al evento.</span>
            </div>
          ) : validator.assigned_events.length === 1 ? (
            <div className="bg-zinc-800 rounded-lg p-3">
              <p className="font-semibold text-white">{validator.assigned_events[0].title}</p>
              <p className="text-xs text-zinc-500 capitalize">
                {new Date(validator.assigned_events[0].date).toLocaleDateString('es-AR', {
                  weekday: 'long', day: 'numeric', month: 'long'
                })}
              </p>
            </div>
          ) : (
            <Select
              value={selectedEvent?.id || ''}
              onValueChange={(value) => {
                const event = validator.assigned_events.find(e => e.id === value);
                setSelectedEvent(event);
              }}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="event-select-trigger">
                <SelectValue placeholder="Seleccionar evento" />
              </SelectTrigger>
              <SelectContent>
                {validator.assigned_events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {selectedEvent && (
          <>
            {/* Stats bar */}
            <div className="grid grid-cols-3 gap-2" data-testid="stats-bar">
              <div className="bg-green-950/40 border border-green-800/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-green-400" data-testid="stats-checked-in">{stats?.checked_in ?? '—'}</p>
                <p className="text-[10px] uppercase tracking-wider text-green-500/80 font-semibold">Ingresados</p>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-white" data-testid="stats-pending">{stats?.pending ?? '—'}</p>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Pendientes</p>
              </div>
              <button
                onClick={() => fetchStats(selectedEvent.id)}
                className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 text-center active:scale-95 transition-transform"
                data-testid="stats-refresh-btn"
              >
                <p className="text-2xl font-black text-[#D4AF37] flex items-center justify-center gap-1">
                  <Users size={18} className="inline" />{stats?.total ?? '—'}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center justify-center gap-1">
                  Total <RefreshCw size={9} />
                </p>
              </button>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-2">
              <Button
                onClick={() => setScanMode('qr')}
                className={`flex-1 h-12 text-base ${scanMode === 'qr' ? 'bg-[#D4AF37] text-black hover:bg-[#C4A030]' : 'bg-zinc-800 text-white hover:bg-zinc-700'}`}
                data-testid="mode-qr-btn"
              >
                <QrCode size={18} className="mr-2" />
                Escanear QR
              </Button>
              <Button
                onClick={() => setScanMode('dni')}
                className={`flex-1 h-12 text-base ${scanMode === 'dni' ? 'bg-[#D4AF37] text-black hover:bg-[#C4A030]' : 'bg-zinc-800 text-white hover:bg-zinc-700'}`}
                data-testid="mode-dni-btn"
              >
                <User size={18} className="mr-2" />
                Por DNI
              </Button>
            </div>

            {/* DNI Mode */}
            {scanMode === 'dni' && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <form onSubmit={(e) => { e.preventDefault(); handleValidate(inputValue); }}>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Ingresá el DNI
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="12345678"
                      className="flex-1 bg-zinc-800 border-zinc-700 text-lg h-12"
                      autoFocus
                      inputMode="numeric"
                      data-testid="dni-input"
                    />
                    <Button
                      type="submit"
                      disabled={validating || !inputValue.trim()}
                      className="bg-[#D4AF37] text-black hover:bg-[#C4A030] px-6 h-12"
                      data-testid="dni-submit-btn"
                    >
                      {validating ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search size={20} />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* QR Mode */}
            {scanMode === 'qr' && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                    {scanning && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                    {scanning ? 'Escaneando...' : cameraLoading ? 'Iniciando cámara...' : 'Cámara apagada'}
                  </span>
                  <Button
                    onClick={() => setCameraEnabled(prev => !prev)}
                    size="sm"
                    disabled={cameraLoading}
                    className={cameraEnabled ? 'bg-zinc-800 text-white hover:bg-zinc-700' : 'bg-[#D4AF37] text-black hover:bg-[#C4A030]'}
                    data-testid="camera-toggle-btn"
                  >
                    {cameraLoading ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    ) : cameraEnabled ? (
                      <CameraOff size={16} className="mr-2" />
                    ) : (
                      <Camera size={16} className="mr-2" />
                    )}
                    {cameraEnabled ? 'Apagar' : 'Prender cámara'}
                  </Button>
                </div>

                {/* Camera container: React NEVER renders inside #qr-reader (library owns it) */}
                <div className="relative w-full max-w-[340px] mx-auto aspect-square rounded-2xl overflow-hidden bg-zinc-950 border border-zinc-800">
                  <div id="qr-reader" className="absolute inset-0 [&_video]:!object-cover [&_video]:!w-full [&_video]:!h-full" />
                  {!scanning && (
                    <div className="absolute inset-0 flex items-center justify-center bg-zinc-900" data-testid="camera-placeholder">
                      <div className="text-center text-zinc-500 p-8">
                        {cameraLoading ? (
                          <div className="w-10 h-10 mx-auto border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <CameraOff size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="text-sm">Cámara apagada.<br />Tocá "Prender cámara" para escanear.</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {scanning && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-[14%] left-[14%] w-8 h-8 border-t-4 border-l-4 border-[#D4AF37] rounded-tl-lg" />
                      <div className="absolute top-[14%] right-[14%] w-8 h-8 border-t-4 border-r-4 border-[#D4AF37] rounded-tr-lg" />
                      <div className="absolute bottom-[14%] left-[14%] w-8 h-8 border-b-4 border-l-4 border-[#D4AF37] rounded-bl-lg" />
                      <div className="absolute bottom-[14%] right-[14%] w-8 h-8 border-b-4 border-r-4 border-[#D4AF37] rounded-br-lg" />
                    </div>
                  )}
                </div>
                <p className="text-center text-xs text-zinc-600 mt-3">
                  Apuntá al QR de la entrada. La validación es automática.
                </p>
              </div>
            )}

            {/* Last Result (compact) */}
            <AnimatePresence mode="wait">
              {lastResult && !overlayResult && (
                <motion.div
                  key={lastResult.timestamp}
                  data-testid="last-result-card"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`border-2 rounded-xl p-4 ${
                    lastResult.success
                      ? 'bg-green-950/30 border-green-500'
                      : lastResult.status === 'already_used'
                      ? 'bg-yellow-950/30 border-yellow-500'
                      : 'bg-red-950/30 border-red-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      lastResult.success ? 'bg-green-500' : lastResult.status === 'already_used' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}>
                      {lastResult.success ? <Check size={20} className="text-white" /> : lastResult.status === 'already_used' ? <AlertCircle size={20} className="text-white" /> : <X size={20} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold ${
                        lastResult.success ? 'text-green-400' : lastResult.status === 'already_used' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {lastResult.message}
                      </p>
                      {(lastResult.ticket || lastResult.invitation) && (
                        <p className="text-sm text-zinc-300 truncate">
                          {lastResult.ticket?.buyer_name || lastResult.invitation?.name} · DNI {lastResult.ticket?.holder_dni || lastResult.invitation?.dni || '—'}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scan History */}
            {scanHistory.length > 0 && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl" data-testid="scan-history">
                <div className="p-4 border-b border-zinc-800">
                  <h3 className="font-semibold text-white">Historial ({scanHistory.length})</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {scanHistory.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border-b border-zinc-800/50 last:border-0"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        item.success ? 'bg-green-500/20' : item.status === 'already_used' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                      }`}>
                        {item.success ? (
                          <Check size={14} className="text-green-400" />
                        ) : item.status === 'already_used' ? (
                          <AlertCircle size={14} className="text-yellow-400" />
                        ) : (
                          <X size={14} className="text-red-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          {item.ticket?.buyer_name || item.invitation?.name || item.searchCode}
                          {(item.ticket?.holder_dni || item.invitation?.dni) && ` · ${item.ticket?.holder_dni || item.invitation?.dni}`}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {new Date(item.timestamp).toLocaleTimeString('es-AR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ValidatorScannerPage;
